import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, SearchBar } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { useMailMessages } from "../hooks";
import { CustomerSelectSearch } from "../../customers/components";
import { cleanObject } from "../../../helpers/clean-object";
import { formatDisplayDate } from "../../../helpers/date";
import { MailDirection } from "../../../__generated__/globalTypes";

interface ISearchForm {
  customerId?: string;
  direction?: string;
}

export const MailMessages: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      customerId: searchParams.get("customerId") ?? "",
      direction: searchParams.get("direction") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/mail-messages",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const customerId = searchParams.get("customerId");
  const direction = searchParams.get("direction");

  const where: any = {};
  if (customerId) where.customerId = +customerId;
  if (direction) where.direction = direction;

  const { data, loading } = useMailMessages({ where });

  return (
    <>
      <Header title="Emails" subtitle="Messages reçus et envoyés" />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder=""
          advancedFilters={
            <>
              <CustomerSelectSearch form={form} name="customerId" />
              <Select label="Sens" className="w-full" {...form.register("direction")}>
                <option value="">Tous</option>
                <option value={MailDirection.Incoming}>Reçus</option>
                <option value={MailDirection.Outgoing}>Envoyés</option>
              </Select>
            </>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !data?.mailMessages?.results?.length ? (
            <EmptyList text="Aucun email" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>&nbsp;</TableCell>
                  <TableCell header>Sujet</TableCell>
                  <TableCell header>De</TableCell>
                  <TableCell header>À</TableCell>
                  <TableCell header>Client</TableCell>
                  <TableCell header>Date</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.mailMessages?.results?.map((message) => (
                  <TableRow key={`mail-message-${message.id}`} hover={false}>
                    <TableCell>
                      {message.direction === MailDirection.Incoming ? (
                        <ArrowDownLeft className="w-4 h-4 text-blue-600" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-green-600" />
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">{message.subject}</TableCell>
                    <TableCell className="max-w-[160px] truncate">
                      {message.fromAddress}
                    </TableCell>
                    <TableCell className="max-w-[160px] truncate">
                      {message.toAddresses}
                    </TableCell>
                    <TableCell>{message.customer?.name ?? "-"}</TableCell>
                    <TableCell>{formatDisplayDate(message.sentAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
};
